import { useLocation } from 'react-router-dom'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import BackgroundSelector from './ui/BackgroundSelector'
import GridToggle from './ui/GridToggle'
import { Eye, EyeOff } from 'lucide-react'

interface AppHeaderProps {
  bgVariant: 'none' | 'dots' | 'lines' | 'cross'
  onBgVariantChange: (variant: 'none' | 'dots' | 'lines' | 'cross') => void
  showGrid: boolean
  onShowGridChange: (show: boolean) => void
  showDevtools: boolean
  onShowDevtoolsChange: (show: boolean) => void
}

export default function AppHeader({
  bgVariant,
  onBgVariantChange,
  showGrid,
  onShowGridChange,
  showDevtools,
  onShowDevtoolsChange,
}: AppHeaderProps) {
  const location = useLocation()

  // Only show canvas controls when on the canvas page
  const isCanvasPage = location.pathname === '/canvas'

  return (
    <header className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 border-b px-4 bg-background">
      {/* Left section - Breadcrumb */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <SidebarTrigger />
        <Separator orientation="vertical" className="h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="#" className="text-muted-foreground">
                Personal
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="#" className="text-muted-foreground">
                Lineage
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-semibold">Blog Pipeline</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Right section - Canvas Controls or Empty Spacer */}
      <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
        {isCanvasPage && (
          <>
            <Button variant="outline" size="sm">
              <span className="text-xs">Saved</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onShowDevtoolsChange(!showDevtools)}
            >
              {showDevtools ? <EyeOff className="h-3.5 w-3.5 mr-1.5" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
              <span className="text-xs">{showDevtools ? 'Hide' : 'Show'} Devtools</span>
            </Button>

            <BackgroundSelector value={bgVariant} onChange={onBgVariantChange} />

            <GridToggle checked={showGrid} onChange={onShowGridChange} />

            <Button variant="default" size="sm">
              <span className="text-xs">Active</span>
            </Button>
          </>
        )}
      </div>
    </header>
  )
}
